import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, FileText, Save, ExternalLink } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

export function DesktopControls() {
  const [fileContent, setFileContent] = useState<string>('');
  const [filePath, setFilePath] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleOpenFile = async () => {
    try {
      setError(null);
      setSuccess(null);
      
      if (!window.electronAPI) {
        setError('Electron API not available');
        return;
      }
      
      const result = await window.electronAPI.fs.openFile();
      
      if (result) {
        if (result.error) {
          setError(`Error opening file: ${result.error}`);
        } else {
          setFileContent(result.content);
          setFilePath(result.filePath);
          setSuccess(`File opened: ${result.filePath}`);
        }
      }
    } catch (err) {
      setError(`Error: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleSaveFile = async () => {
    try {
      setError(null);
      setSuccess(null);
      
      if (!window.electronAPI) {
        setError('Electron API not available');
        return;
      }
      
      const result = await window.electronAPI.fs.saveFile(fileContent);
      
      if (result) {
        if (result.success) {
          setFilePath(result.filePath);
          setSuccess(`File saved: ${result.filePath}`);
        } else {
          setError(`Error saving file: ${result.message}`);
        }
      }
    } catch (err) {
      setError(`Error: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const openExternalLink = () => {
    if (window.electronAPI) {
      window.electronAPI.system.openExternal('https://electronjs.org');
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Desktop File Operations</CardTitle>
        <CardDescription>
          Interact with the native file system using Electron
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {success && (
          <Alert className="mb-4 bg-green-50 text-green-800 border-green-200">
            <AlertTitle>Success</AlertTitle>
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}
        
        <div className="space-y-4">
          <div className="flex space-x-4">
            <Button onClick={handleOpenFile} className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Open File
            </Button>
            
            <Button onClick={handleSaveFile} variant="secondary" className="flex items-center gap-2">
              <Save className="h-4 w-4" />
              Save File
            </Button>
            
            <Button onClick={openExternalLink} variant="outline" className="flex items-center gap-2">
              <ExternalLink className="h-4 w-4" />
              Open Electron Website
            </Button>
          </div>
          
          {filePath && (
            <p className="text-sm text-gray-500">
              Current file: {filePath}
            </p>
          )}
          
          <Textarea 
            value={fileContent}
            onChange={(e) => setFileContent(e.target.value)}
            placeholder="File content will appear here. You can also type content to save to a file."
            className="min-h-[200px]"
          />
        </div>
      </CardContent>
      <CardFooter className="text-sm text-gray-500">
        This component demonstrates desktop-specific features using Electron's APIs.
      </CardFooter>
    </Card>
  );
} 